/************************************************
 * src/components/LicenseManagement.js
 ************************************************/
import React, { useState, useEffect, useCallback } from 'react';
import axios from './axiosInstance';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';

import './styles/LicenseGenerator.css';
import RedmineExtendedData from './RedmineExtendedData';
import RedmineLicenseRequests from './RedmineLicenseRequests';
import LicenseHistory from './LicenseHistory';
import { initiateSocket, getSocket } from './socket';
import ProtectedComponent from './ProtectedComponent';
import { AuthContext } from './context/AuthContext';

const LicenseManagement = () => {
  // LICENSE GEN STATES
  const [licenseId, setLicenseId] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [numOfStores, setNumOfStores] = useState(1);
  const [serverType, setServerType] = useState('CENTRAL');
  const [layoutDesigner, setLayoutDesigner] = useState('INCLUDE');
  const [paymentLicenseType, setPaymentLicenseType] = useState('PAYMENT');
  const [output, setOutput] = useState('');
  const [selectedCompany, setSelectedCompany] = useState({ value: '(Standalone)', label: '(Standalone)' });
  const [companyTagId, setCompanyTagId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // COMPANY SELECTOR STATES
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  // REDMINE AGGREGATOR STATES
  const [licenseRequestsData, setLicenseRequestsData] = useState([]); // AIMS-only
  const [extendedData, setExtendedData] = useState([]);               // both trackers
  const [maxSubprojects, setMaxSubprojects] = useState(0);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [activeTab, setActiveTab] = useState('requests');

  // REPLY-TO-TICKET
  const [ticketReplyModal, setTicketReplyModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [licenseContentForModal, setLicenseContentForModal] = useState('');
  const [closeTicket, setCloseTicket] = useState(false);

  // FILTER for open vs all
  const [filterOption, setFilterOption] = useState('all');

  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);

  // ============ FETCH COMPANIES ============
  // Pulls a unified list from /license-companies/combined: our saved
  // LicenseCompany docs plus any companies extracted from cached Redmine
  // tickets. Each row has a `source` ('internal' | 'redmine' | 'both') so the
  // UI can show provenance and decide whether to find-or-create on selection.
  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get('/license-companies/combined');
      setCompanies(res.data.companies || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  }, []);

  useEffect(() => {
    fetchAllRedmineData();
    fetchCompanies();

    const token = localStorage.getItem('token');
    if (token) {
      initiateSocket(token);
    }
    const socket = getSocket();
    if (socket) {
      socket.emit('join', 'lic-mgmt');
      socket.on('aggregatedData', (data) => {
        console.log('Socket aggregator data:', data);
        if (data.licenseRequests) setLicenseRequestsData(data.licenseRequests);
        if (data.extendedData) setExtendedData(data.extendedData);
        if (data.maxSubprojects !== undefined) setMaxSubprojects(data.maxSubprojects);
        if (data.lastUpdatedTime) {
          setLastUpdatedTime(data.lastUpdatedTime);
        }
      });
      socket.on('dataDeltas', (deltas) => {
        console.log('Received data deltas:', deltas);
        // simplest approach => re-fetch
        fetchAllRedmineData();
      });
      socket.on('connect_error', (err) => {
        console.error('Socket error:', err.message);
      });
    }
    return () => {
      if (socket) {
        socket.emit('leave', 'lic-mgmt');
        socket.off('aggregatedData');
        socket.off('dataDeltas');
        socket.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Combined endpoint returns the full merged set; filtering happens client-side
  // via filteredCompanies below, so no debounced re-fetch is needed.

  // ============ 1) FETCH AGGREGATOR DATA ============
  const fetchAllRedmineData = async () => {
    try {
      setIsFetching(true);
      const res = await axios.get('/redmine/aggregatedData');
      const { licenseRequests, extendedData, maxSubprojects, lastUpdatedTime } = res.data;
      setLicenseRequestsData(licenseRequests || []);
      setExtendedData(extendedData || []);
      setMaxSubprojects(maxSubprojects || 0);
      setLastUpdatedTime(lastUpdatedTime);
    } catch (err) {
      console.error('Error fetching aggregator data:', err);
      setErrorMessage('Failed to fetch aggregator data.');
    } finally {
      setIsFetching(false);
    }
  };

  // ============ COMPANY HANDLERS ============
  const handleCompanySelect = async (company) => {
    setSelectedCompany({ value: company.name, label: company.name, _id: company._id });
    setCompanySearch(company.name);
    setShowCompanyDropdown(false);

    // Redmine-only entries don't yet exist in our LicenseCompany collection.
    // Silently import on selection so subsequent dropdown loads show it as
    // 'both' and the company is searchable even when Redmine is unreachable.
    if (company.source === 'redmine') {
      try {
        const res = await axios.post('/license-companies/find-or-create', { name: company.name });
        if (res.data?.company?._id) {
          setSelectedCompany({
            value: res.data.company.name,
            label: res.data.company.name,
            _id: res.data.company._id,
          });
        }
        fetchCompanies();
      } catch (err) {
        console.error('Error importing Redmine company:', err);
      }
    }
  };

  const handleCompanyInputChange = (e) => {
    const value = e.target.value;
    setCompanySearch(value);
    setShowCompanyDropdown(true);
    if (value.trim()) {
      setSelectedCompany({ value: value, label: value });
    } else {
      setSelectedCompany({ value: '(Standalone)', label: '(Standalone)' });
    }
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) return;
    try {
      const res = await axios.post('/license-companies', { name: newCompanyName.trim() });
      setShowAddCompanyModal(false);
      setNewCompanyName('');
      setSelectedCompany({ value: res.data.company.name, label: res.data.company.name, _id: res.data.company._id });
      setCompanySearch(res.data.company.name);
      fetchCompanies();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create company');
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  const showAddOption = companySearch.trim() &&
    !filteredCompanies.some(c => c.name.toLowerCase() === companySearch.toLowerCase().trim());

  // ============ 3) GENERATE LICENSE =============
  const checkInputs = () => {
    const missing = [];
    if (!selectedCompany) missing.push('Company');
    if (!licenseId) missing.push('License ID');
    if (!expiryDate) missing.push('Expiry Date');
    if (!numOfStores) missing.push('Number of Stores');
    if (!serverType) missing.push('Server Type');
    if (!layoutDesigner) missing.push('Layout Designer');
    if (!paymentLicenseType) missing.push('Payment License Type');
    if (missing.length > 0) {
      alert(`Please fill in: ${missing.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleShowModal = () => {
    if (checkInputs()) {
      setShowModal(true);
    }
  };

  const handleGenerate = async () => {
    setShowModal(false);
    const dateObj = new Date(expiryDate);
    dateObj.setDate(dateObj.getDate() + 1);
    const formatted = dateObj
      .toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      .replace(/\//g, '-');

    const payload = {
      paymentLicenseType,
      licenseId,
      expiryDate: formatted,
      numOfStores,
      serverType,
      layoutDesigner,
      companyTagId,
      company: selectedCompany.label,
      ticketId: selectedTicketId,
      ticketSubject: licenseRequestsData.find(t => t.ticket_id === selectedTicketId)?.subject
    };
    try {
      const r = await axios.post('/sharepoint/sign-license', payload);
      if (r.data && r.data.signedLicense) {
        setOutput(r.data.signedLicense);

        // Track company usage if not standalone
        if (selectedCompany.value !== '(Standalone)') {
          try {
            await axios.post('/license-companies/find-or-create', { name: selectedCompany.label });
          } catch (companyErr) {
            console.error('Error tracking company usage:', companyErr);
          }
        }

        // Copy to clipboard
        try {
          await navigator.clipboard.writeText(r.data.signedLicense);
          setNotificationMessage('License generated and copied to clipboard!');
        } catch (clipErr) {
          setNotificationMessage('License generated!');
        }
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);

        // Open redmine reply modal if there's a ticket
        if (selectedTicketId) {
          setLicenseContentForModal(r.data.signedLicense);
          setTicketReplyModal(true);
        }
      } else {
        setErrorMessage('Failed to generate license: no signedLicense returned.');
      }
    } catch (err) {
      console.error('Error generating license:', err);
      if (err.response && err.response.status === 401) {
        alert('Unauthorized. Please log in again.');
        navigate('/login');
      } else {
        setErrorMessage('Server error generating license.');
      }
    }
  };

  // ============ 4) REPLY-TO-TICKET ==============
  const handleReplyToTicket = async () => {
    try {
      await axios.post('/redmine/reply', {
        ticketId: selectedTicketId,
        licenseMessage: licenseContentForModal,
        closeTicket,
      });
      alert('Reply posted to Redmine successfully.');
    } catch (err) {
      console.error('Error replying to ticket:', err);
      alert('Failed to reply to the ticket.');
    }
    setTicketReplyModal(false);
  };

  // ============ RENDER =============
  return (
    <ProtectedComponent functionCode="OP04" functionName="License Management">
      <div className="MainContent">
      {/* LICENSE GEN FORM */}
      <div className="LicenseGeneratorContent">
        <h2>License Generator</h2>
        <table className="license-generator-table">
          <tbody>
            <tr>
              <td>
                <label>Company:</label>
                <div className="company-selector-wrapper" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Type to search or add company..."
                    value={companySearch}
                    onChange={handleCompanyInputChange}
                    onFocus={() => setShowCompanyDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
                    style={{ width: '100%' }}
                  />
                  {showCompanyDropdown && (companySearch || companies.length > 0) && (
                    <div className="company-dropdown" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#2a3b50',
                      border: '1px solid #4a5568',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000
                    }}>
                      {/* Standalone option */}
                      <div
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #4a5568' }}
                        onMouseDown={() => {
                          setSelectedCompany({ value: '(Standalone)', label: '(Standalone)' });
                          setCompanySearch('');
                          setShowCompanyDropdown(false);
                        }}
                      >
                        (Standalone)
                      </div>
                      {/* Filtered companies */}
                      {filteredCompanies.map(c => {
                        const badge =
                          c.source === 'redmine'
                            ? { text: 'Redmine', bg: '#1f4e79', fg: '#9bcdff' }
                            : c.source === 'both'
                              ? { text: 'Saved + Redmine', bg: '#2e5d3a', fg: '#b6e5c2' }
                              : { text: 'Saved', bg: '#3a3a3a', fg: '#cccccc' };
                        return (
                          <div
                            key={c._id || `redmine-${c.name}`}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #3a4a5a',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '8px',
                            }}
                            onMouseDown={() => handleCompanySelect(c)}
                          >
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {c.name}
                              {c.licenseCount > 0 && (
                                <span style={{ fontSize: '0.8em', color: '#888', marginLeft: '8px' }}>
                                  ({c.licenseCount} licenses)
                                </span>
                              )}
                              {c.ticketCount > 0 && (
                                <span style={{ fontSize: '0.8em', color: '#888', marginLeft: '8px' }}>
                                  ({c.ticketCount} tickets)
                                </span>
                              )}
                            </span>
                            <span
                              style={{
                                fontSize: '0.7em',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                backgroundColor: badge.bg,
                                color: badge.fg,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {badge.text}
                            </span>
                          </div>
                        );
                      })}
                      {/* Add new company option */}
                      {showAddOption && (
                        <div
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            backgroundColor: '#1a5f2a',
                            color: '#90ee90'
                          }}
                          onMouseDown={() => {
                            setNewCompanyName(companySearch.trim());
                            setShowAddCompanyModal(true);
                            setShowCompanyDropdown(false);
                          }}
                        >
                          + Add "{companySearch.trim()}" as new company
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </td>
              <td>
                <label>License ID:</label>
                <input
                  type="text"
                  placeholder="Enter License ID"
                  value={licenseId}
                  onChange={(e) => setLicenseId(e.target.value)}
                />
              </td>
              <td>
                <label>Expiry Date:</label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>Number of Stores:</label>
                <input
                  type="number"
                  value={numOfStores}
                  onChange={(e) => setNumOfStores(Number(e.target.value))}
                />
              </td>
              <td>
                <label>Server Type:</label>
                <select value={serverType} onChange={(e) => setServerType(e.target.value)}>
                  <option value="CENTRAL">CENTRAL</option>
                  <option value="SINGLE">SINGLE</option>
                </select>
              </td>
              <td>
                <label>Layout Designer:</label>
                <select value={layoutDesigner} onChange={(e) => setLayoutDesigner(e.target.value)}>
                  <option value="INCLUDE">INCLUDE</option>
                  <option value="EXCLUDE">EXCLUDE</option>
                </select>
              </td>
            </tr>
            <tr>
              <td>
                <label>Payment License Type:</label>
                <select
                  value={paymentLicenseType}
                  onChange={(e) => setPaymentLicenseType(e.target.value)}
                >
                  <option value="PAYMENT">PAYMENT</option>
                  <option value="DEFAULT">DEFAULT</option>
                </select>
              </td>
              <td colSpan="2">
                <textarea
                  readOnly
                  value={output}
                  placeholder="Generated License Output"
                  style={{ width: '100%', height: '80px' }}
                />
              </td>
            </tr>
            <tr>
              <td colSpan="3" style={{ textAlign: 'center' }}>
                <button onClick={handleShowModal}>Generate License</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* TABS + LAST UPDATED */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
        <div className="tabs" style={{ display: 'flex' }}>
          <div
            className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            License Requests
          </div>
          <div
            className={`tab ${activeTab === 'extended' ? 'active' : ''}`}
            onClick={() => setActiveTab('extended')}
          >
            Extended Data
          </div>
          {user && (user.isSuperAdmin || user.role === 'admin') && (
            <div
              className={`tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              History
            </div>
          )}
        </div>
        <div style={{ marginLeft: 'auto', color: 'white', fontSize: '0.75rem' }}>
          {lastUpdatedTime ? (
            <p style={{ margin: 0 }}>
              Current as of: {new Date(lastUpdatedTime).toLocaleString()}
            </p>
          ) : (
            <p style={{ margin: 0 }}>Loading last updated time...</p>
          )}
        </div>
      </div>

      {/* TAB CONTENT */}
      <div className="licenseRequestDiv">
        {activeTab === 'requests' && (
          <RedmineLicenseRequests
            licenseRequestsData={licenseRequestsData}  // AIMS only
            filterOption={filterOption}
            setFilterOption={setFilterOption}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            licenseId={licenseId}
            setLicenseId={setLicenseId}
            companyTagId={companyTagId}
            setCompanyTagId={setCompanyTagId}
            tickets={[]}       // if needed
            setTickets={() => {}}
          />
        )}

        {activeTab === 'extended' && (
          <RedmineExtendedData
            data={extendedData}         // both trackers
            maxSubprojects={maxSubprojects}
          />
        )}

        {activeTab === 'history' && user && (user.isSuperAdmin || user.role === 'admin') && (
          <LicenseHistory />
        )}
      </div>

      {/* ADD COMPANY MODAL */}
      <Modal
        isOpen={showAddCompanyModal}
        onRequestClose={() => setShowAddCompanyModal(false)}
        style={{
          content: {
            backgroundColor: '#2a3b50',
            color: 'white',
            padding: '20px',
            maxWidth: '400px',
            margin: 'auto',
          },
        }}
      >
        <h2>Add New Company</h2>
        <div style={{ marginTop: '15px' }}>
          <label>Company Name:</label>
          <input
            type="text"
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            placeholder="Enter company name"
            style={{ width: '100%', marginTop: '5px', padding: '8px' }}
            autoFocus
          />
        </div>
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setShowAddCompanyModal(false)}
            style={{ backgroundColor: '#555', color: '#fff', padding: '8px 15px', borderRadius: '5px' }}
          >
            Cancel
          </button>
          <button
            onClick={handleAddCompany}
            style={{ backgroundColor: '#2e7d32', color: '#fff', padding: '8px 15px', borderRadius: '5px' }}
          >
            Add Company
          </button>
        </div>
      </Modal>

      {/* CONFIRM GENERATE MODAL */}
      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        style={{
          content: {
            backgroundColor: 'black',
            color: 'white',
            padding: '20px',
          },
        }}
      >
        <h2>Summary</h2>
        <p>Company: {selectedCompany?.label}</p>
        <p>License ID: {licenseId}</p>
        <p>Expiry Date: {expiryDate}</p>
        <p>Number of Stores: {numOfStores}</p>
        <p>Server Type: {serverType}</p>
        <p>Layout Designer: {layoutDesigner}</p>
        <p>Payment License Type: {paymentLicenseType}</p>
        <div style={{ marginTop: '20px' }}>
          <button onClick={handleGenerate} style={{ marginRight: '10px' }}>
            Confirm
          </button>
          <button onClick={() => setShowModal(false)}>
            Cancel
          </button>
        </div>
      </Modal>

      {/* REPLY TO TICKET MODAL */}
      <Modal
        isOpen={ticketReplyModal}
        onRequestClose={() => setTicketReplyModal(false)}
        style={{
          content: {
            backgroundColor: 'black',
            color: 'white',
            padding: '20px',
          },
        }}
      >
        <h2>Reply to Ticket</h2>
        <p>Would you like to reply to the Redmine ticket with the generated license?</p>
        <textarea
          style={{ width: '100%', height: '120px' }}
          value={licenseContentForModal}
          onChange={(e) => setLicenseContentForModal(e.target.value)}
        />
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
          <label style={{ color: 'white', marginRight: '8px' }}>
            Close the ticket after replying
          </label>
          <input
            type="checkbox"
            checked={closeTicket}
            onChange={(e) => setCloseTicket(e.target.checked)}
          />
        </div>
        <div style={{ marginTop: '20px' }}>
          <button onClick={handleReplyToTicket} style={{ marginRight: '10px' }}>
            Yes, reply
          </button>
          <button onClick={() => setTicketReplyModal(false)}>
            No, cancel
          </button>
        </div>
      </Modal>

      {/* NOTIFICATION */}
      {showNotification && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: 'green',
            color: 'white',
            padding: '10px',
            borderRadius: '5px',
            zIndex: 1002,
          }}
        >
          {notificationMessage}
        </div>
      )}

      {errorMessage && (
        <p style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</p>
      )}
    </div>
    </ProtectedComponent>
  );
};

export default LicenseManagement;
